// useDeviceHeading — provides device compass heading in degrees (0 = North, clockwise).
// Handles iOS permission flow + Android fallback via alpha.
// Usage:
//   const { heading, permission, requestPermission, supported } = useDeviceHeading();
//   call requestPermission() from a user gesture (button click) on iOS.
import { useEffect, useRef, useState, useCallback } from "react";

function screenAngleAdjustment() {
  // Rotate heading so it reflects the direction the user is "looking" through the
  // long axis of the device (back camera), regardless of screen orientation.
  const angle =
    (typeof screen !== "undefined" && screen.orientation && typeof screen.orientation.angle === "number")
      ? screen.orientation.angle
      : (typeof window !== "undefined" && typeof window.orientation === "number" ? window.orientation : 0);
  return angle || 0;
}

export function useDeviceHeading() {
  const [heading, setHeading] = useState(null); // degrees 0..360
  const [permission, setPermission] = useState("prompt"); // 'granted' | 'denied' | 'prompt'
  const [supported, setSupported] = useState(true);
  const listenerAttachedRef = useRef(false);

  const handler = useCallback((event) => {
    let h = null;
    // iOS Safari exposes compass heading directly
    if (typeof event.webkitCompassHeading === "number" && !isNaN(event.webkitCompassHeading)) {
      h = event.webkitCompassHeading;
    } else if (event.absolute === true && typeof event.alpha === "number") {
      // Android: alpha is counter-clockwise from north → convert to clockwise.
      h = 360 - event.alpha;
    } else if (typeof event.alpha === "number") {
      // Fallback (not absolute): approximate
      h = 360 - event.alpha;
    }
    if (h == null || isNaN(h)) return;
    // Compensate for screen rotation so "forward" is always the long axis of the device.
    h = (h + screenAngleAdjustment() + 360) % 360;
    setHeading(h);
  }, []);

  const attachListener = useCallback(() => {
    if (listenerAttachedRef.current) return;
    // Prefer absolute orientation when available (Android)
    const absEventSupported = "ondeviceorientationabsolute" in window;
    if (absEventSupported) {
      window.addEventListener("deviceorientationabsolute", handler, true);
    }
    window.addEventListener("deviceorientation", handler, true);
    listenerAttachedRef.current = true;
  }, [handler]);

  const requestPermission = useCallback(async () => {
    if (typeof DeviceOrientationEvent === "undefined") {
      setSupported(false);
      return "unsupported";
    }
    // iOS 13+ requires explicit permission request from a user gesture
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      try {
        const state = await DeviceOrientationEvent.requestPermission();
        setPermission(state === "granted" ? "granted" : "denied");
        if (state === "granted") attachListener();
        return state;
      } catch {
        setPermission("denied");
        return "denied";
      }
    }
    // Android / desktop: no permission dialog, just attach.
    setPermission("granted");
    attachListener();
    return "granted";
  }, [attachListener]);

  useEffect(() => {
    return () => {
      if (listenerAttachedRef.current) {
        window.removeEventListener("deviceorientation", handler, true);
        window.removeEventListener("deviceorientationabsolute", handler, true);
        listenerAttachedRef.current = false;
      }
    };
  }, [handler]);

  return { heading, permission, requestPermission, supported };
}

// Great-circle bearing (initial) from point A to point B in degrees clockwise from North.
export function bearingDegrees(from, to) {
  if (!from || !to) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Signed angular difference target - current, normalized to -180..180.
export function angularDelta(target, current) {
  if (target == null || current == null) return null;
  let d = ((target - current + 540) % 360) - 180;
  return d;
}
